/**
 * @file The funnel every attachment download goes through, and the byte source
 * of the email attachment flow. Consumers: `useAttachments` (download, email),
 * `useExtractSchedule` (the bytes Extract schedule sends to PDF intake, with the
 * version it read) and `AttachmentPreviewOverlay`, which uses it for its
 * Download control only —
 * the overlay resolves its own preview bytes, because a preview wants a URL for
 * an `<iframe>` where a download always wants bytes. What the two share is
 * `resolveAttachmentVersionSource`, which owns the `layout` switch; this file
 * holds no layout knowledge of its own.
 *
 * On the lane that writes a file, the name the browser saves under comes from
 * `savedAttachmentFileName` rather than from the display name directly: a name
 * carrying no extension would leave the operating system nothing to open the
 * file by.
 *
 * Every failure returns `{ success: false, error }` and never throws, so a
 * caller that ignores `success` reports a silent failure as a success. A cancel
 * is not a failure: it returns `{ success: false, cancelled: true }` with no
 * `error`, and a caller must report it as neither.
 *
 * Each download owns one `AbortController`, held in a ref and replaced — after
 * aborting the one before it — at the start of the next. One controller per
 * download is what keeps a cancel local to the download the user stopped; a
 * long-lived shared signal would cancel every read that consulted it.
 *
 * A chunked version is the one read worth watching on a row of its own: it
 * holds the whole file in memory before a single byte reaches disk. Such a read
 * opens an `assemble` transfer in `attachmentTransfersStore`, reports bytes held
 * as each chunk lands, and registers that same controller as the row's Cancel,
 * so the row's Cancel and the hook's `cancelDownload` stop one download and the
 * same one.
 */
import { useState, useCallback, useEffect, useRef } from 'react';
import { readAttachmentDocument } from '../../services/readAttachmentDocument';
import { resolveAttachmentVersionSource } from '../../storage/resolveAttachmentVersionSource';
import { savedAttachmentFileName } from '../../storage/savedAttachmentFileName';
import type { ChunkedAttachmentReadProgress } from '../../storage/chunkedAttachment';
import { attachmentTransfersActions } from '../../store/attachmentTransfersStore';
import { Attachment, AttachmentVersion } from '#types/System.types';

/**
 * Download result interface for attachment downloads
 */
export interface DownloadResult {
  /** Whether the download was successful */
  success: boolean;
  /** Name of the downloaded file */
  fileName: string;
  /** URL of the downloaded file */
  fileUrl?: string;
  /** Error that occurred during download, if any */
  error?: Error;
  /**
   * Set when the user stopped the download. `success` is false and `error` is
   * absent, because nothing went wrong — callers report it as neither.
   */
  cancelled?: true;
  /** Blob of the file content when returnBlob is true */
  blob?: Blob;
  /** Content type of the file */
  contentType?: string;
  /** The version actually read, set on success: the requested one, or the latest when none was requested. */
  versionId?: string;
  versionNumber?: number;
}

/**
 * Props for downloading an attachment
 */
export interface AttachmentDownloadProps {
  /** File ID to download from */
  fileUid: string;
  /** Filename to save as */
  fileName: string;
  /** Version ID of the specific version to download. If omitted, downloads the latest version */
  versionId?: string;
  /** Whether to return the blob instead of triggering a download */
  returnBlob?: boolean;
}

/**
 * Result of the useAttachmentDownload hook
 */
interface AttachmentDownloadResult {
  /** Function to download an attachment version */
  downloadAttachment: (
    props: AttachmentDownloadProps,
  ) => Promise<DownloadResult>;
  /** Stops the download in flight. Stable, and a no-op when nothing is downloading. */
  cancelDownload: () => void;
  /** Progress percentage of the current download (0-100) */
  progress: number;
  /** Whether a download is in progress */
  isDownloading: boolean;
}

/**
 * The body length the reads can actually be divided by, or 0 for "not
 * knowable". `content-length` is the only figure a response offers and it is
 * the decoded length only when the body was not encoded: on a `gzip` body it
 * counts compressed bytes while the reads deliver decompressed ones, so
 * dividing by it would race past 100 about a third of the way in. So a
 * `content-encoding` other than `identity` retires the total before the first
 * read rather than after the overrun — a bar that never starts is honest, a bar
 * that reaches 98 and freezes for the rest of the transfer is the hang this
 * work exists to remove.
 */
const divisibleBodyLength = (headers: Headers): number => {
  const encoding = headers.get('content-encoding');
  if (encoding !== null && encoding.trim().toLowerCase() !== 'identity')
    return 0;

  const declared = Number(headers.get('content-length'));
  return Number.isSafeInteger(declared) && declared > 0 ? declared : 0;
};

/**
 * Drains an object version's response body into one `Blob`, reporting the
 * percentage transferred as each piece arrives. The count rises once per read
 * and cannot pass 100, because a total the body overruns is retired on the spot
 * and never reinstated — the reads only ever climb, so nothing later could make
 * it trustworthy again. That leaves the bar wherever the last honest reading put
 * it, which is why `divisibleBodyLength` refuses an encoded body up front: this
 * latch is the safety net for a server that lies without saying so, not the
 * first line of defence. A response with no `body` at all falls back to
 * `blob()`, which is what this lane did before it streamed.
 *
 * Each read is retained as its own `Blob`, so only one read's bytes are JS heap
 * at a time and the rest live in the browser's blob store, which may page them
 * to disk. That is the same shape `readChunkedAttachmentVersion` uses and the
 * reason a download far larger than the tab's heap limit survives at all;
 * accumulating `ArrayBuffer`s and joining them at the end would hold the whole
 * file twice over. The `slice()` is a type matter, not an aliasing one: a
 * default reader hands out a fresh view per read and reuses nothing, but its
 * `Uint8Array<ArrayBufferLike>` is not a `BlobPart` under this repo's
 * TypeScript, because `ArrayBufferLike` admits `SharedArrayBuffer`.
 *
 * An abort mid-stream errors the body, so `read` rejects and the caller's
 * `signal.aborted` branch reports the download as cancelled, not failed.
 */
const readStreamedResponse = async (
  response: Response,
  contentType: string,
  onPercent: (percent: number) => void,
): Promise<Blob> => {
  if (!response.body) return response.blob();

  const reader = response.body.getReader();
  const parts: Blob[] = [];
  let totalBytes = divisibleBodyLength(response.headers);
  let bytesTransferred = 0;

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    parts.push(new Blob([value.slice().buffer]));
    bytesTransferred += value.byteLength;
    if (bytesTransferred > totalBytes) totalBytes = 0;
    if (totalBytes > 0)
      onPercent(Math.round((bytesTransferred / totalBytes) * 100));
  }

  return new Blob(parts, { type: contentType });
};

/** What turning one version into bytes needs. */
interface VersionBytesRequest {
  fileUid: string;
  fileName: string;
  attachment: Attachment;
  versionId: string;
  version: AttachmentVersion;
  /** This download's own signal; both lanes consult it. */
  signal: AbortSignal;
  /** The real fraction transferred, from whichever lane is doing the work. */
  onPercent: (percent: number) => void;
  /**
   * The chunked lane's own byte counts, for the `assemble` transfer row. Absent
   * on an object version, which holds nothing worth a row.
   */
  onChunkedProgress?: (progress: ChunkedAttachmentReadProgress) => void;
}

/**
 * Hook for downloading attachments with state management
 *
 * Uses Firebase Storage getDownloadURL to generate a fresh download URL
 * from the storage path, ensuring URLs are always up-to-date.
 */
export const useAttachmentDownload = (): AttachmentDownloadResult => {
  const [progress, setProgress] = useState<number>(0);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const cancelDownload = useCallback((): void => {
    abortControllerRef.current?.abort();
  }, []);

  useEffect(() => () => abortControllerRef.current?.abort(), []);

  /**
   * Turns one version into bytes. The `layout` switch lives in
   * `resolveAttachmentVersionSource`; a download differs from a preview only in
   * that it always ends in bytes, so an object version's URL is streamed here
   * while an assembled chunked version is already what a download needs.
   *
   * Both lanes report the real fraction transferred through `onPercent`: the
   * chunked reader counts verified bytes against the manifest's `fileSize`, and
   * the object lane counts the body's own bytes against `content-length`.
   * Neither ever invents one — see `readStreamedResponse` for what the object
   * lane does when the total is not knowable. An empty chunked version reports
   * both figures as 0, which is 100% transferred, not a division to attempt.
   */
  const readVersionBytes = useCallback(
    async ({
      fileUid,
      fileName,
      attachment,
      versionId,
      version,
      signal,
      onPercent,
      onChunkedProgress,
    }: VersionBytesRequest): Promise<{
      blob: Blob;
      contentType: string;
      fileUrl?: string;
    }> => {
      const source = await resolveAttachmentVersionSource({
        fileUid,
        versionId,
        version,
        attachment,
        fileName,
        signal,
        onProgress: (progress: ChunkedAttachmentReadProgress) => {
          const { bytesTransferred, totalBytes } = progress;
          onPercent(
            totalBytes === 0
              ? 100
              : Math.round((bytesTransferred / totalBytes) * 100),
          );
          onChunkedProgress?.(progress);
        },
      });

      if (source.kind === 'blob') {
        return { blob: source.blob, contentType: source.contentType };
      }

      const response = await fetch(source.url, { signal });
      const contentType =
        response.headers.get('content-type') || 'application/octet-stream';

      return {
        blob: await readStreamedResponse(response, contentType, onPercent),
        contentType,
        fileUrl: source.url,
      };
    },
    [],
  );

  /**
   * Downloads a version of an attachment.
   *
   * A cancel is recognised by the download's own signal rather than by the shape
   * of what was thrown, so the chunked reader's `cancelled` reason and the
   * object lane's `fetch` abort report the same way.
   */
  const downloadAttachment = useCallback(
    async (props: AttachmentDownloadProps): Promise<DownloadResult> => {
      const { fileUid, fileName, versionId, returnBlob } = props;

      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      /**
       * Every write to `progress` goes through here, so a download that has been
       * superseded cannot move the bar of the one that replaced it. `finally`
       * guards `isDownloading` on the same predicate.
       */
      const report = (percent: number): void => {
        if (abortControllerRef.current === controller) setProgress(percent);
      };

      setIsDownloading(true);
      report(0);

      try {
        const attachment = await readAttachmentDocument(fileUid);

        if (!attachment) {
          throw new Error('File not found');
        }

        if (
          !attachment.versions ||
          Object.keys(attachment.versions).length === 0
        ) {
          throw new Error('No versions found for this file');
        }

        /**
         * The requested version, or the highest-numbered one. Taken as an entry
         * rather than a value because the id is what pins the version's manifest
         * to the version being read.
         */
        const entries = Object.entries(attachment.versions);
        const [selectedVersionId, version] =
          versionId && attachment.versions[versionId]
            ? ([versionId, attachment.versions[versionId]] as const)
            : entries.reduce(
                (latest, current) =>
                  current[1].versionNumber > latest[1].versionNumber
                    ? current
                    : latest,
                entries[0],
              );

        /**
         * Only a chunked version assembles, so only a chunked version gets a row.
         * The transfer is opened before the first chunk is fetched and closed in
         * every exit, cancellation included, so no row can outlive its read. Its
         * Cancel is this download's own controller, so stopping the row and
         * stopping the hook are the same act.
         */
        const transferId =
          version.layout === 'chunked' ? crypto.randomUUID() : null;

        if (transferId) {
          attachmentTransfersActions.startTransfer({
            transferId,
            fileUid,
            fileName,
            kind: 'assemble',
            bytesTransferred: 0,
            totalBytes: version.sizeBytes ?? 0,
          });
          attachmentTransfersActions.allowCancel(transferId, () =>
            controller.abort(),
          );
        }

        const { blob, contentType, fileUrl } = await readVersionBytes({
          fileUid,
          fileName,
          attachment,
          versionId: selectedVersionId,
          version,
          signal: controller.signal,
          onPercent: report,
          onChunkedProgress: transferId
            ? ({ bytesTransferred, totalBytes }) => {
                attachmentTransfersActions.reportBytes(
                  transferId,
                  bytesTransferred,
                  totalBytes,
                );
              }
            : undefined,
        }).finally(() => {
          if (transferId) {
            attachmentTransfersActions.endTransfer(transferId);
          }
        });

        report(100);

        if (returnBlob) {
          return {
            success: true,
            fileName,
            ...(fileUrl ? { fileUrl } : {}),
            blob,
            contentType,
            versionId: selectedVersionId,
            versionNumber: version.versionNumber,
          };
        }

        const url = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = savedAttachmentFileName(fileName, contentType);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setTimeout(() => window.URL.revokeObjectURL(url), 100);

        return {
          success: true,
          fileName,
          ...(fileUrl ? { fileUrl } : {}),
          contentType,
          versionId: selectedVersionId,
          versionNumber: version.versionNumber,
        };
      } catch (error) {
        if (controller.signal.aborted) {
          return { success: false, fileName, cancelled: true };
        }

        console.error(`Failed to download ${fileName}:`, error);
        return {
          success: false,
          fileName,
          error:
            error instanceof Error
              ? error
              : new Error(`Failed to download ${fileName}`),
        };
      } finally {
        if (abortControllerRef.current === controller) {
          abortControllerRef.current = null;
          setIsDownloading(false);
        }
      }
    },
    [readVersionBytes],
  );

  return {
    downloadAttachment,
    cancelDownload,
    progress,
    isDownloading,
  };
};
